/**
 * Toolbar controls: From/To/Arrange/Export custom dropdowns + swap button.
 */

import { store } from './state';
import type { InputFormat, OutputFormat, Direction } from '../core/types';

/** Sample code for each diagram type so users can see how each works */
const SAMPLE_CODE: Partial<Record<InputFormat, string>> = {
  flowchart: `flowchart TD
  A([Submit Request]) --> B[Review Details]
  B --> C{Budget Approved?}
  C -- No --> D[Return to Requester]
  D --> B
  C -- Yes --> E[Send to Vendor]
  E --> F[Receive Goods]
  F --> G{Quality Check Passed?}
  G -- No --> H[File Rejection Report]
  H --> I([Close Request])
  G -- Yes --> J[Process Payment]
  J --> I`,

  state: `stateDiagram-v2
  [*] --> Idle

  Idle --> Processing : submit
  Processing --> Review : auto_check
  Processing --> Failed : error

  state Review {
    [*] --> Pending
    Pending --> Approved : approve
    Pending --> Rejected : reject
    Approved --> [*]
    Rejected --> [*]
  }

  Review --> Complete : finalize
  Failed --> Idle : retry
  Complete --> [*]`,

  class: `classDiagram
  class Animal {
    +String name
    +int age
    +makeSound() void
    +move() void
  }

  class Dog {
    +String breed
    +fetch() void
    +makeSound() void
  }

  class Cat {
    +bool indoor
    +purr() void
    +makeSound() void
  }

  class Shelter {
    -List~Animal~ animals
    +addAnimal(Animal a) void
    +removeAnimal(String name) Animal
    +getCount() int
  }

  Animal <|-- Dog : inherits
  Animal <|-- Cat : inherits
  Shelter o-- Animal : houses
  Shelter --> Dog : manages`,

  er: `erDiagram
  CUSTOMER ||--o{ ORDER : places
  CUSTOMER {
    int id PK
    string first_name
    string last_name
    string email UK
    date created_at
  }

  ORDER ||--|{ ORDER_LINE : contains
  ORDER {
    int id PK
    int customer_id FK
    date order_date
    string status
    decimal total_amount
  }

  ORDER_LINE }o--|| PRODUCT : references
  ORDER_LINE {
    int id PK
    int order_id FK
    int product_id FK
    int quantity
    decimal unit_price
  }

  PRODUCT {
    int id PK
    string name
    string sku UK
    decimal price
    int stock_quantity
  }

  ORDER ||--o| PAYMENT : "paid via"
  PAYMENT {
    int id PK
    int order_id FK
    string method
    decimal amount
    date paid_at
  }`,
};

const FROM_OPTIONS: Array<{ value: InputFormat; label: string; disabled?: boolean }> = [
  { value: 'flowchart', label: 'Mermaid \u2013 Flow Chart' },
  { value: 'class', label: 'Mermaid \u2013 Class Diagram' },
  { value: 'state', label: 'Mermaid \u2013 State Diagram' },
  { value: 'er', label: 'Mermaid \u2013 ER Diagram' },
  { value: 'mindmap', label: 'Mermaid \u2013 Mind Map (Coming Soon)', disabled: true },
  { value: 'gantt', label: 'Mermaid \u2013 Gantt (Coming Soon)', disabled: true },
  { value: 'timeline', label: 'Mermaid \u2013 Timeline (Coming Soon)', disabled: true },
  { value: 'drawio-xml', label: 'Draw.io XML' },
];

/** Map from inputFormat → available output formats */
const TO_OPTIONS: Record<string, Array<{ value: OutputFormat; label: string }>> = {
  flowchart: [
    { value: 'drawio-flowchart', label: 'Flowchart' },
    { value: 'drawio-activity', label: 'UML Activity Diagram' },
  ],
  class: [{ value: 'drawio-class', label: 'Class Diagram' }],
  state: [{ value: 'drawio-state', label: 'State Diagram' }],
  er: [{ value: 'drawio-er', label: 'ER Diagram' }],
  'drawio-xml': [{ value: 'mermaid', label: 'Mermaid Flowchart' }],
};

const SIMPLE_ARRANGE_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'TD', label: 'Vertical' },
  { value: 'LR', label: 'Horizontal' },
];

const FULL_ARRANGE_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'TD', label: 'Top to Bottom' },
  { value: 'LR', label: 'Left to Right' },
  { value: 'RL', label: 'Right to Left' },
  { value: 'BT', label: 'Bottom to Top' },
];

const ER_ARRANGE_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'compact', label: 'Compact' },
  { value: 'spaced', label: 'Spaced' },
];

function getArrangeOptions(inputFormat: string) {
  if (inputFormat === 'er' || inputFormat === 'class') return ER_ARRANGE_OPTIONS;
  return inputFormat === 'mindmap' ? FULL_ARRANGE_OPTIONS : SIMPLE_ARRANGE_OPTIONS;
}

const EXPORT_OPTIONS = [
  { value: 'drawio', label: 'Drawio' },
  { value: 'svg', label: 'SVG' },
  { value: 'png', label: 'PNG' },
];

interface CustomDropdown {
  wrap: HTMLElement;
  trigger: HTMLButtonElement;
  menu: HTMLUListElement;
  value: string;
}

let fromDropdown: CustomDropdown;
let toDropdown: CustomDropdown;
let arrangeDropdown: CustomDropdown;
let exportDropdown: CustomDropdown;
let swapBtn: HTMLButtonElement;

function setupDropdown(
  triggerId: string,
  menuId: string,
  wrapId: string,
): CustomDropdown {
  const wrap = document.getElementById(wrapId) as HTMLElement;
  const trigger = document.getElementById(triggerId) as HTMLButtonElement;
  const menu = document.getElementById(menuId) as HTMLUListElement;
  return { wrap, trigger, menu, value: '' };
}

function populateDropdown(
  dd: CustomDropdown,
  options: Array<{ value: string; label: string; disabled?: boolean }>,
  selectedValue?: string,
): void {
  dd.menu.innerHTML = '';
  for (const opt of options) {
    const li = document.createElement('li');
    li.dataset.value = opt.value;
    li.textContent = opt.label;
    if (opt.value === selectedValue) {
      li.classList.add('selected');
    }
    if (opt.disabled) {
      li.classList.add('disabled');
      li.style.opacity = '0.5';
      li.style.pointerEvents = 'none';
    }
    dd.menu.appendChild(li);
  }
  // Set trigger text to selected option
  const selected = options.find((o) => o.value === selectedValue) ?? options[0];
  if (selected) {
    dd.trigger.textContent = selected.label;
    dd.value = selected.value;
  }
}

function openDropdown(dd: CustomDropdown): void {
  closeAllDropdowns();
  dd.wrap.classList.add('open');
}

function closeAllDropdowns(): void {
  document.querySelectorAll('.custom-select.open').forEach((el) => {
    el.classList.remove('open');
  });
}

export function initControls(): void {
  fromDropdown = setupDropdown('from-select-trigger', 'from-select-menu', 'from-select-wrap');
  toDropdown = setupDropdown('to-select-trigger', 'to-select-menu', 'to-select-wrap');
  arrangeDropdown = setupDropdown('arrange-select-trigger', 'arrange-select-menu', 'arrange-select-wrap');
  exportDropdown = setupDropdown('export-select-trigger', 'export-select-menu', 'export-select-wrap');
  swapBtn = document.getElementById('swap-btn') as HTMLButtonElement;

  if (!fromDropdown.wrap || !toDropdown.wrap) return;

  // Populate From dropdown
  populateDropdown(fromDropdown, FROM_OPTIONS, store.get().inputFormat);

  // Populate Arrange
  populateDropdown(arrangeDropdown, getArrangeOptions(store.get().inputFormat), store.get().direction);

  // Populate Export
  populateDropdown(exportDropdown, EXPORT_OPTIONS, 'drawio');

  // Initial To options
  updateToOptions();

  // --- Click handlers for triggers ---
  fromDropdown.trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    if (fromDropdown.wrap.classList.contains('open')) {
      closeAllDropdowns();
    } else {
      openDropdown(fromDropdown);
    }
  });

  toDropdown.trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    if (toDropdown.wrap.classList.contains('open')) {
      closeAllDropdowns();
    } else {
      openDropdown(toDropdown);
    }
  });

  arrangeDropdown.trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    if (arrangeDropdown.wrap.classList.contains('open')) {
      closeAllDropdowns();
    } else {
      openDropdown(arrangeDropdown);
    }
  });

  exportDropdown.trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    if (exportDropdown.wrap.classList.contains('open')) {
      closeAllDropdowns();
    } else {
      openDropdown(exportDropdown);
    }
  });

  // --- Menu item click handlers ---
  fromDropdown.menu.addEventListener('click', (e) => {
    const li = (e.target as HTMLElement).closest('li');
    if (!li?.dataset.value) return;
    const format = li.dataset.value as InputFormat;
    // Load sample code when switching to a new diagram type
    const sample = SAMPLE_CODE[format];
    if (sample) {
      store.set({ inputFormat: format, input: sample });
    } else {
      store.set({ inputFormat: format });
    }
    // Reset direction to 'auto' if current value isn't valid for new format
    const currentDir = store.get().direction;
    if (format !== 'mindmap' && (currentDir === 'BT' || currentDir === 'RL')) {
      store.set({ direction: 'auto' });
    }
    if (format !== 'er' && format !== 'class' && (currentDir === 'compact' || currentDir === 'spaced')) {
      store.set({ direction: 'auto' });
    }
    populateDropdown(fromDropdown, FROM_OPTIONS, format);
    populateDropdown(arrangeDropdown, getArrangeOptions(format), store.get().direction);
    updateToOptions();
    closeAllDropdowns();
    triggerConvert();
  });

  toDropdown.menu.addEventListener('click', (e) => {
    const li = (e.target as HTMLElement).closest('li');
    if (!li?.dataset.value) return;
    const format = li.dataset.value as OutputFormat;
    // Sync preview mode default based on output format
    const previewMode = format.startsWith('drawio') ? 'xml' : 'mermaid';
    store.set({ outputFormat: format, previewMode });
    const inputFormat = store.get().inputFormat;
    const options = TO_OPTIONS[inputFormat] ?? TO_OPTIONS['flowchart'];
    populateDropdown(toDropdown, options, format);
    closeAllDropdowns();
    triggerConvert();
  });

  arrangeDropdown.menu.addEventListener('click', (e) => {
    const li = (e.target as HTMLElement).closest('li');
    if (!li?.dataset.value) return;
    const dir = li.dataset.value as Direction;
    store.set({ direction: dir });
    populateDropdown(arrangeDropdown, getArrangeOptions(store.get().inputFormat), dir);
    closeAllDropdowns();
    triggerConvert();
  });

  exportDropdown.menu.addEventListener('click', (e) => {
    const li = (e.target as HTMLElement).closest('li');
    if (!li?.dataset.value) return;
    populateDropdown(exportDropdown, EXPORT_OPTIONS, li.dataset.value);
    exportDropdown.value = li.dataset.value;
    closeAllDropdowns();
  });

  if (swapBtn) {
    swapBtn.addEventListener('click', onSwap);
  }

  // Close dropdowns when clicking outside
  document.addEventListener('click', () => {
    closeAllDropdowns();
  });

  // Sync from state changes (e.g. auto-detect)
  store.subscribe((state) => {
    populateDropdown(fromDropdown, FROM_OPTIONS, state.inputFormat);
    populateDropdown(arrangeDropdown, getArrangeOptions(state.inputFormat), state.direction);
    updateToOptions();
  });
}

function updateToOptions(): void {
  const format = store.get().inputFormat;
  const options = TO_OPTIONS[format] ?? TO_OPTIONS['flowchart'];

  // Set default output format
  const current = store.get().outputFormat;
  const hasMatch = options.some((o) => o.value === current);
  if (!hasMatch) {
    store.set({ outputFormat: options[options.length - 1].value as OutputFormat });
  }

  populateDropdown(toDropdown, options, store.get().outputFormat);
}

function onSwap(): void {
  const state = store.get();
  if (state.inputFormat !== 'drawio-xml' && state.outputFormat !== 'mermaid') {
    store.set({ inputFormat: 'drawio-xml', outputFormat: 'mermaid' });
  } else if (state.inputFormat === 'drawio-xml') {
    store.set({ inputFormat: 'flowchart', outputFormat: 'drawio-activity' });
  }
  populateDropdown(fromDropdown, FROM_OPTIONS, store.get().inputFormat);
  updateToOptions();
  triggerConvert();
}

/** Expose export format value for export.ts */
export function getExportFormat(): string {
  return exportDropdown?.value ?? 'drawio';
}

function triggerConvert(): void {
  document.dispatchEvent(new CustomEvent('ex-mermaid:convert'));
}
